import { Box } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { ErrorMessage, Field, Formik, FormikErrors, FormikProps } from "formik";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { DatePicker } from "rsuite";
import { isAfter, isBefore } from "rsuite/esm/internals/utils/date";
import { number, object, string } from "yup";

import { CalendarIcon } from "components/icons/CalendarIcon";
import FailedRedNotificationIcon from "components/icons/FailedRedNotificationIcon";
import Info from "components/icons/Info";
import LocationPointer from "components/icons/LocationPointer";
import PreviewIcon from "components/icons/PreviewIcon";
import { BUNDLE_ONLY_LICENSES, CorporateBundle } from "components/licenses/bundles";
import {
    AI_LICENSES,
    BAM_ADDON_LICENSES,
    BLANCCO_TOKEN_ID,
    createLicenseTypes,
    deduceAddOnsBasedOnBundle,
    FEATURE_LICENSES,
    fetchDynamicTierAddOns,
    fetchDynamicTierLicenses,
    GENERIC_ID_LENGTH_FOR_SALESFORCE,
    GENERIC_ID_OPTION_FOR_SALESFORCE,
    NON_DELIVERABLE_FEATURE_LICENSES,
    OPPORTUNITY_ID_VALIDATION_FORMAT_1,
    OPPORTUNITY_ID_VALIDATION_FORMAT_2,
    SALESFORCE_CONTRACT_ID_VALIDATION_FORMAT_1,
    SALESFORCE_ID_VALIDATION_FORMAT_1,
    SALESFORCE_LEAD_ID_VALIDATION_FORMAT_1,
    showOnlyTokenLicense,
} from "components/licenses/common";
import {
    createDefaultSelectedLicense,
    DeliveryForm,
    License,
    MAX_CASE_NUMBER_LENGTH,
    OPPORTUNITY_ID_LENGTH,
    SALESFORCE_ID_LENGTH,
} from "components/licenses/delivery-history/DeliveryFormContent";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import style from "components/tenants/add-tenant/add-tenant.scss";
import { AutomaticDeletion } from "components/tenants/AutomaticDeletion";
import { DeliveryTab, isAnyLicenseInvalid, populateWithLicenses } from "components/tenants/DeliveryTab";
import { DrawbackPrevention } from "components/tenants/DrawbackPrevention";
import PricingTierView from "components/tenants/PricingTierView";
import Tooltip from "components/tooltip/Tooltip";
import Heading from "components/typography/heading/Heading";
import { getCountries } from "domain/countries";
import { EMAIL_MAX_LENGTH, INPUT_MAX_LENGTH, NAME_MAX_LENGTH, NOTES_MAX_LENGTH } from "domain/globalConstants";
import { EmsConfiguration, LicenseData } from "domain/licenses";
import {
    CombinedTier,
    CONTRACT_TYPE_PRODUCTION,
    CONTRACT_TYPE_TRIAL,
    ContractType,
    LicensingModel,
    OldLicensingModelTier,
    TenantContractStatus,
    TenantsData,
    TenantType,
} from "domain/tenants";
import { generateTenantTypeToTranslationMap, generateTierToTranslationMap } from "domain/tierRelatedMaps";
import { CheckEmailAvailability } from "domain/users";
import { featureFlagService, FLAG_AI_ADDONS, FLAG_BAM_ADDONS } from "services/feature/FeatureFlagService";
import { extractTenantLicenseRateUuid, licenseService, ProductToRateList } from "services/licenses/LicenseService";
import {
    getCurrentTenantDetails,
    isUserParentInternal,
    showAllowedTierList,
} from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userService } from "services/user/users/UserService";
import buttons from "styles/buttons.scss";
import defaultColor from "styles/colors/default-color.scss";
import form from "styles/form.scss";
import { isStringBlank } from "utils/commonFunctions";
import { formatDatePickerDate, formatDateWithoutTime, MONTHS, toUtcEndOfDay } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    submitEventHandler: (values: FormValues, selectedLicenses: License[]) => void;
    closeHandler: () => void;
    regionsList: string[];
    theme: typeof defaultColor;
    tenantLicenses: LicenseData[];
    parentExpirationDate: Date | undefined;
    parentHierarchyLevel: number;
    tenantType: TenantType;
    tenantTier: CombinedTier;
    licensingModel: LicensingModel;
    rateVersions?: ProductToRateList[];
    disableSubmit: (toDisable: boolean) => void;
    validateOnChange?: boolean;
    parentDrawbackPrevention: boolean;
    loadingHandler: (value: boolean) => void;
    parentContractType: ContractType;
}

interface CodedName {
    name: string;
    code: string;
}

export interface GeneralRequiredFormValues {
    customerName: string;
    region: string;
    country: string;
    mainContactName: string;
    email: string;
    salesforceAccountId: string;
    salesforceLeadId?: string;
}

export interface DeliveryDetailsFormValues {
    deliveryDetails: DeliveryForm;
}

export interface SettingsFormValues {
    ems: EmsConfiguration;
}

export interface FormValues extends GeneralRequiredFormValues, DeliveryDetailsFormValues, SettingsFormValues {
    type: TenantType;
    tier: CombinedTier;
    notes: string;
    contractType: ContractType;
    automaticDeletion?: boolean;
    expirationDate?: Date;
    licensingModel: LicensingModel;
    salesforceContractId?: string;
    salesforceContractState?: TenantContractStatus | string;
    salesforceContactEmail?: string;
    salesforceContractEndDate?: Date;
    salesforceLeadId?: string;
    drawbackPrevention?: boolean;
}

function resolveTierLicenses(
    tierLicenseIds: string[],
    allLicenses: { productName: string; productId: string }[]
): { productName: string; productId: string }[] {
    return allLicenses.filter((license) => tierLicenseIds.includes(license.productId));
}

export function isTokenLicense(productId: string): boolean {
    return productId === BLANCCO_TOKEN_ID;
}

export function generateSelectableLicenses(
    dropdownLicenses: { productName: string; productId: string }[],
    selectedTier: CombinedTier,
    licensingModel: LicensingModel
) {
    const deliverableLicenses: { productName: string; productId: string }[] = [];
    const allAvailableLicenses = createLicenseTypes(true).filter(
        (element) =>
            (featureFlagService.isFeatureEnabled(FLAG_AI_ADDONS) || !AI_LICENSES.includes(element.productId)) &&
            (featureFlagService.isFeatureEnabled(FLAG_BAM_ADDONS) || !BAM_ADDON_LICENSES.includes(element.productId))
    );
    const tokenInUse = showOnlyTokenLicense(licensingModel);
    if (licensingModel === LicensingModel.OLD_MODEL) {
        dropdownLicenses.push(
            ...allAvailableLicenses.filter(
                (element) =>
                    !NON_DELIVERABLE_FEATURE_LICENSES.includes(element.productId) &&
                    !BUNDLE_ONLY_LICENSES.includes(element.productId)
            )
        );
        return;
    }
    allAvailableLicenses.forEach((eachInAllLicenses) => {
        if (!tokenInUse && isTokenLicense(eachInAllLicenses.productId)) {
            return;
        }
        deliverableLicenses.push(eachInAllLicenses);
    });
    const tierLicenses = deduceTierLicenses(selectedTier, false);
    const addOnLicenses = fetchDynamicTierAddOns(selectedTier);

    if (tierLicenses && tierLicenses.length > 0) {
        const filteredTierLicenses = resolveTierLicenses(tierLicenses, deliverableLicenses);
        if (tokenInUse) {
            dropdownLicenses.push(...extractAddOns(filteredTierLicenses, addOnLicenses));
            dropdownLicenses.push(...deliverableLicenses.filter((each) => isTokenLicense(each.productId)));
        } else {
            dropdownLicenses.push(...filteredTierLicenses);
        }
    } else {
        if (tokenInUse) {
            dropdownLicenses.push(...deliverableLicenses.filter((each) => isTokenLicense(each.productId)));
        } else {
            dropdownLicenses.push(...deliverableLicenses);
        }
    }
}

export function deduceTierLicenses(
    tier: CombinedTier,
    withoutAddOns: boolean,
    withoutFeatureLicenses?: boolean,
    rateCardLicenses?: string[]
): string[] {
    const dynamicLicenses = fetchDynamicTierLicenses(tier);
    if (dynamicLicenses.length <= 0) {
        return [];
    }
    const bundleAddOns = fetchDynamicTierAddOns(tier);
    const tierLicenses = withoutAddOns
        ? dynamicLicenses.filter(
              (each) => !bundleAddOns.includes(each) && !(withoutFeatureLicenses && FEATURE_LICENSES.includes(each))
          )
        : dynamicLicenses;
    return rateCardLicenses ? tierLicenses.filter((each) => rateCardLicenses.includes(each)) : tierLicenses;
}

function extractAddOns(availableLicenses: { productName: string; productId: string }[], addOnLicenses: string[]) {
    return availableLicenses.filter((each) => addOnLicenses.includes(each.productId));
}

export function isProductAddOn(productId: string, selectedTier: CombinedTier) {
    const dynamicAddOns = fetchDynamicTierAddOns(selectedTier);
    if (dynamicAddOns.length > 0) {
        return dynamicAddOns.includes(productId);
    }
    return deduceAddOnsBasedOnBundle(selectedTier).includes(productId);
}

export const AddTenantForm = (props: Props): JSX.Element => {
    const { t } = useTranslation();

    const today = new Date();
    const defaultTrialExpiration = today;
    defaultTrialExpiration.setDate(today.getDate() + 14);
    const [automaticDeletionDefaultValue, setAutomaticDeletionDefaultValue] = React.useState(true);

    const defaultValues = deduceDefaultTenantOptions(props.tenantTier, props.tenantType, props.licensingModel);
    const currentTenantDetails = getCurrentTenantDetails();
    const countries = getCountries(t).sort((a, b) => (a.name > b.name ? 1 : -1));
    const regions = generateRegions();
    const [selectedContractType, setSelectedContractType] = React.useState<ContractType>(props.parentContractType);
    const [selectedExpirationDate, setSelectedExpirationDate] = React.useState<Date | undefined>(
        props.parentExpirationDate
            ? props.parentExpirationDate
            : selectedContractType === "TRIAL"
              ? defaultTrialExpiration
              : undefined
    );
    const [selectedDateAsString, setSelectedDateAsString] = React.useState<string>(t("Common.never"));
    const [salesforceContractEndDate, setSalesforceContractEndDate] = React.useState<Date | undefined>();
    const [selectedSalesforceContractState, setSelectedSalesforceContractState] = React.useState<string>("");
    const [selectedContractEndDateAsString, setSelectedContractEndDateAsString] = React.useState<string>(
        t("Common.select")
    );
    const [nameLoader, setNameLoader] = React.useState<boolean>(false);
    const isUserInternal = () => {
        return props.tenantType == "INTERNAL";
    };
    const [cleaned, setCleaned] = React.useState(false);
    const [state, setState] = React.useState({
        miniLoader: false,
        notesCharactersLeft: NOTES_MAX_LENGTH,
        emsIdUseDefault: true,
        formValues: {
            customerName: "",
            type: defaultValues.defaultType,
            region: currentTenantDetails.region || "",
            tier: defaultValues.defaultTier,
            country: "",
            contractType: props.parentContractType,
            notes: "",
            mainContactName: "",
            email: "",
            salesforceAccountId: "",
            salesforceContractId: "",
            salesforceContractState: "",
            salesforceLeadId: "",
            salesforceContactEmail: selectedSalesforceContractState,
            salesforceContractEndDate: salesforceContractEndDate,
            expirationDate: selectedExpirationDate,
            ems: {
                emsId: "",
                hlEntitlements: false,
                slEntitlements: false,
                availableSlActivations: 0,
                usedSlActivations: 0,
                lastSynced: "",
            },
            drawbackPrevention: isUserInternal() ? false : undefined,
            deliveryDetails: {
                deliveryType: "",
                caseNumber: "",
                opportunityId: "",
                notes: "",
                amount: null,
                expirationDate: "",
                tokenRateVersion: "",
                targetDate: "",
            },
            licensingModel: defaultValues.defaultModel,
        },
        selectedLicenses: populateWithLicenses(
            defaultValues.defaultTier,
            defaultValues.defaultModel,
            props.tenantLicenses,
            t
        ),
    });
    const toCreateDeliveryTab = props.tenantLicenses.length > 0;
    const maxDateString = "2090-12-31T23:59:59Z";

    const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(true);
    const checkLicenseErrors = () => {
        const hasAmountError = state.selectedLicenses.some(
            (license) => license.licensesToAdd < 0 || license.totalOfLicenses < 0
        );
        setIsSubmitDisabled(hasAmountError);
    };

    React.useEffect(() => {
        setAutomaticDeletionDefaultValue(true);
    }, []);

    React.useEffect(() => {
        checkLicenseErrors();
    }, [state.selectedLicenses]);

    React.useEffect(() => {
        props.loadingHandler(state.miniLoader || nameLoader);
    }, [state.miniLoader, nameLoader]);

    function deduceDefaultTenantOptions(
        parentTier: CombinedTier,
        parentType: TenantType,
        parentModel: LicensingModel
    ): {
        defaultType: TenantType;
        defaultTier: CombinedTier;
        defaultModel: LicensingModel;
    } {
        if (["CORPORATE", "PROCESSOR", "CUSTOMER", "MOBILE_PROCESSOR"].includes(parentType)) {
            return {
                defaultType: parentType,
                defaultTier: parentTier,
                defaultModel: parentModel,
            };
        }
        if (
            parentType === "INTERNAL" ||
            (parentModel === LicensingModel.BUNDLE_WITH_TOKEN && parentType === "CHANNEL")
        ) {
            return {
                defaultType: "CORPORATE",
                defaultTier: CorporateBundle.ESSENTIALS,
                defaultModel: LicensingModel.BUNDLE_WITH_TOKEN,
            };
        }
        return {
            defaultType: "CUSTOMER",
            defaultTier: OldLicensingModelTier.STANDARD,
            defaultModel: parentModel,
        };
    }

    function generateRegions(): Array<CodedName> {
        const awsRegions = [
            { name: t("AddCustomerForm.UsEastNorthVirginia"), code: "us-east-1" },
            { name: t("AddCustomerForm.UsEastOhio"), code: "us-east-2" },
            { name: t("AddCustomerForm.UsWestOregon"), code: "us-west-2" },
            { name: t("AddCustomerForm.UsWestNorthernCalifornia"), code: "us-west-1" },
            { name: t("AddCustomerForm.SouthAmericaSaoPaulo"), code: "sa-east-1" },
            { name: t("AddCustomerForm.EuropeFrankfurt"), code: "eu-central-1" },
            { name: t("AddCustomerForm.EuropeLondon"), code: "eu-west-2" },
            { name: t("AddCustomerForm.EuropeParis"), code: "eu-west-3" },
            { name: t("AddCustomerForm.MiddleEastBahrain"), code: "me-south-1" },
            { name: t("AddCustomerForm.EuropeIreland"), code: "eu-west-1" },
            { name: t("AddCustomerForm.EuropeMilan"), code: "eu-south-1" },
            { name: t("AddCustomerForm.EuropeStockholm"), code: "eu-north-1" },
            { name: t("AddCustomerForm.AwsAfricaCapeTown"), code: "af-south-1" },
            { name: t("AddCustomerForm.MainlandChinaBeijing"), code: "cn-north-1" },
            { name: t("AddCustomerForm.MainlandChinaNingxia"), code: "cn-northwest-1" },
            { name: t("AddCustomerForm.AsiaPacificSydney"), code: "ap-southeast-2" },
            { name: t("AddCustomerForm.AsiaPacificOsaka"), code: "ap-northeast-3" },
            { name: t("AddCustomerForm.AsiaPacificTokyo"), code: "ap-northeast-1" },
            { name: t("AddCustomerForm.AsiaPacificMumbai"), code: "ap-south-1" },
            { name: t("AddCustomerForm.AsiaPacificSeoul"), code: "ap-northeast-2" },
            { name: t("AddCustomerForm.AsiaPacificHongKong"), code: "ap-east-1" },
        ];

        if (props.regionsList) {
            return awsRegions
                .filter((region) => props.regionsList.some((reg: string) => region.code === reg))
                .sort((a, b) => (a.name > b.name ? 1 : -1));
        } else {
            return [{ name: t("AddCustomerForm.EuropeIreland"), code: "eu-west-1" }];
        }
    }

    function createField(
        id: keyof EmsConfiguration,
        label: string,
        testId: string,
        value: string | boolean | number,
        type: "text" | "checkbox" | "number" | "label",
        inErrorState: boolean,
        required: boolean,
        handleChange?: (event: React.ChangeEvent) => void,
        errorTestId?: string,
        disabled?: boolean
    ): JSX.Element {
        const className = classNames(form.input, {
            [form.fixedWidthInput]: ["text", "number"].includes(type),
            [form.inputError]: inErrorState,
        });
        const valueAttribute = type === "checkbox" ? "checked" : "value";
        const attributes = Object.assign(
            {
                id,
                name: "ems." + id,
                className,
                "data-testid": testId,
                onChange: handleChange,
                type,
                [valueAttribute]: value,
                disabled,
            },
            type !== "number" ? {} : { min: 0 }
        );
        const element =
            type === "label" ? (
                <span className={form.fixedWidthInput} data-testid={testId}>
                    {value}
                </span>
            ) : (
                <Field {...attributes} />
            );

        const labels =
            type === "checkbox" ? (
                <label className={form.container}>
                    {element}
                    <span className={form.checkmark} />
                </label>
            ) : (
                <>{element}</>
            );

        return (
            <div className={form.formFields}>
                <div className={form.formFieldsColumnDirection}>
                    <span className={form.optional}>{required ? "" : t("Common.optional")}</span>
                    <label htmlFor={id} className={form.label}>
                        {label}
                    </label>
                </div>
                <div className={form.formFieldsColumnDirection}>
                    {labels}
                    <div className={form.error} data-testid={errorTestId}>
                        <ErrorMessage component="div" name={attributes.name} className={form.error} />
                    </div>
                </div>
            </div>
        );
    }

    function createCustomerNameSection(formikProps: FormikProps<FormValues>) {
        const loader = nameLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );
        return (
            <div className={classNames(form.formFields, style.topMarginGap)}>
                <label htmlFor="customerName" className={form.label}>
                    {t("Common.customerName")}
                </label>

                <div className={form.formFieldsColumnDirection}>
                    <input
                        id="customerName"
                        className={classNames(form.input, form.fixedWidthInput, {
                            [form.inputError]: formikProps.errors.customerName,
                        })}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.nameInput.itself}
                        onChange={formikProps.handleChange}
                        onBlur={formikProps.handleBlur}
                        value={formikProps.values.customerName}
                    />
                    {loader}
                    <div
                        className={form.error}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.nameInput.errorLabel}
                    >
                        <ErrorMessage name="customerName" />
                    </div>
                </div>
            </div>
        );
    }

    function updateSelectedLicenses(tier: CombinedTier, licensingModel: LicensingModel) {
        if (licensingModel === LicensingModel.OLD_MODEL) {
            setState((prevState) => ({
                ...prevState,
                selectedLicenses: [createDefaultSelectedLicense(t)],
            }));
        } else {
            setState((prevState) => {
                return {
                    ...prevState,
                    selectedLicenses: populateWithLicenses(tier, licensingModel, props.tenantLicenses, t),
                };
            });
        }
    }

    function generateContractTypeOptions(): ContractType[] {
        const currentContractType = currentTenantDetails.contractType;
        if (props.parentContractType === "TRIAL" || currentContractType === "TRIAL") {
            return ["TRIAL"];
        }
        return ["TRIAL", "PRODUCTION"];
    }

    function generateTenantOptions(parentType: TenantType, selectedModel: LicensingModel): TenantType[] {
        if (parentType === "INTERNAL") {
            if (selectedModel === LicensingModel.OLD_MODEL) {
                return ["CHANNEL", "CUSTOMER"];
            }
            return ["CORPORATE", "PROCESSOR", "SMB", "CHANNEL", "MOBILE_RETAIL", "MOBILE_PROCESSOR"];
        }
        if (parentType === "CHANNEL") {
            if (selectedModel === LicensingModel.OLD_MODEL) {
                return ["CHANNEL", "CUSTOMER"];
            }
            return ["CORPORATE", "PROCESSOR", "SMB", "CHANNEL", "MOBILE_RETAIL", "MOBILE_PROCESSOR"];
        }
        return [parentType];
    }

    function createCustomerTypeSection(formikProps: FormikProps<FormValues>) {
        const { t } = useTranslation();
        const tenantToTranslationKeyMap = generateTenantTypeToTranslationMap(t);

        const validTenantTypes: TenantType[] = generateTenantOptions(
            props.tenantType,
            formikProps.values.licensingModel
        );

        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <label htmlFor="type" className={classNames(form.label)}>
                        {t("AddCustomerForm.tenantType")}
                    </label>
                    <select
                        id="type"
                        className={classNames(form.select, form.fixedWidthInput)}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.typeSelect.itself}
                        onChange={(event) => {
                            formikProps.handleChange(event);
                            const oldModel = formikProps.values.licensingModel == LicensingModel.OLD_MODEL;
                            const preselectedTier = showAllowedTierList(
                                "",
                                event.target.value as TenantType,
                                props.tenantTier,
                                oldModel
                            )[0];
                            formikProps.setFieldValue("tier", preselectedTier);

                            updateSelectedLicenses(preselectedTier, formikProps.values.licensingModel);
                        }}
                        value={formikProps.values.type}
                    >
                        {validTenantTypes.map((value, index) => (
                            <option key={index} value={value}>
                                {tenantToTranslationKeyMap.get(value)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }

    function createRegionSection(formikProps: FormikProps<FormValues>) {
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <label htmlFor="region" className={form.label}>
                        {t("AddCustomerForm.customerFormRegion")}:
                    </label>
                    <select
                        id="region"
                        className={classNames(form.select, form.fixedWidthInput, {
                            [form.inputError]: formikProps.errors.region,
                        })}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.regionSelect.itself}
                        onChange={formikProps.handleChange}
                        value={formikProps.values.region}
                    >
                        {regions &&
                            regions.map((value, index) => (
                                <option key={index} value={value.code}>
                                    {value.name}
                                </option>
                            ))}
                    </select>
                    <div>
                        <a
                            href="https://aws.amazon.com/about-aws/global-infrastructure/regions_az/"
                            rel="noreferrer"
                            target="_blank"
                        >
                            <div
                                className={classNames(style.locationPointer, style.regionAndInfoContainer)}
                                tabIndex={0}
                            >
                                <LocationPointer borderColor={props.theme.iconFillColor} />
                                <div className={style.locationPointerText}>{t("AddCustomerForm.mapDescription")}</div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    function createLicensingModelSection(formikProps: FormikProps<FormValues>) {
        const licensingModelToTranslationKey = new Map<string, string>([
            [LicensingModel.OLD_MODEL, t("AddCustomerForm.licensingModel.old")],
            [LicensingModel.BUNDLE, t("AddCustomerForm.licensingModel.bundles")],
            [LicensingModel.BUNDLE_WITH_TOKEN, t("AddCustomerForm.licensingModel.bundlesWithToken")],
        ]);
        const licensingModelOptions: string[] = [];
        switch (props.tenantType) {
            case "INTERNAL":
                licensingModelOptions.push(...[LicensingModel.OLD_MODEL, LicensingModel.BUNDLE_WITH_TOKEN]);
                break;
            case "CUSTOMER":
                licensingModelOptions.push(LicensingModel.OLD_MODEL);
                break;
            default:
                licensingModelOptions.push(props.licensingModel);
                break;
        }
        return (
            <>
                <div className={form.formFields}>
                    <label htmlFor="licensingModel" className={classNames(form.label)}>
                        {t("AddCustomerForm.licensingModel.label")}
                    </label>
                    <select
                        id={"licensingModel"}
                        className={classNames(form.select, form.fixedWidthInput)}
                        onChange={(event) => {
                            formikProps.handleChange(event);
                            const selectedLicensingModel = event.target.value as LicensingModel;
                            const oldModel = selectedLicensingModel == LicensingModel.OLD_MODEL;
                            const selectedTenantType = oldModel ? "CUSTOMER" : "CORPORATE";
                            formikProps.setFieldValue("type", selectedTenantType);
                            const preselectedTier = showAllowedTierList(
                                "",
                                selectedTenantType,
                                props.tenantTier,
                                oldModel
                            )[0];
                            formikProps.setFieldValue("tier", preselectedTier);
                            updateSelectedLicenses(preselectedTier, selectedLicensingModel);
                            if (selectedLicensingModel !== LicensingModel.OLD_MODEL) {
                                formikProps.setFieldValue(
                                    "deliveryDetails.tokenRateVersion",
                                    extractTenantLicenseRateUuid(props.rateVersions, selectedTenantType)
                                );
                            }
                        }}
                        value={formikProps.values.licensingModel}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.licensingModelSelect.itself}
                    >
                        {licensingModelOptions.map((value, index) => (
                            <option key={index} value={value}>
                                {licensingModelToTranslationKey.get(value)}
                            </option>
                        ))}
                    </select>
                </div>
            </>
        );
    }

    function createTierSection(formikProps: FormikProps<FormValues>) {
        const [pricingTierModalVisible, setPricingTierModalVisible] = React.useState(false);
        const theme = useTheme();
        const oldLicensingModel = formikProps.values.licensingModel == LicensingModel.OLD_MODEL;
        const allowedTiers = [];
        if (props.tenantType === "INTERNAL" || props.tenantType === "CUSTOMER" || props.tenantType === "CHANNEL") {
            allowedTiers.push(...showAllowedTierList("", formikProps.values.type, props.tenantTier, oldLicensingModel));
        } else {
            allowedTiers.push(props.tenantTier);
        }

        return (
            <>
                <div className={style.regionAndInfoContainer}>
                    <div className={form.formFields}>
                        <label htmlFor="tier" className={classNames(form.label)}>
                            {oldLicensingModel ? t("AddCustomerForm.tier") : t("AddCustomerForm.blanccoBundles")}
                        </label>
                        <select
                            id="tier"
                            className={classNames(form.select, form.fixedWidthInput)}
                            data-testid={testIds.workArea.tenant.manageTenantDialog.details.tierSelect.itself}
                            onChange={(event) => {
                                formikProps.handleChange(event);
                                updateSelectedLicenses(
                                    event.target.value as CombinedTier,
                                    formikProps.values.licensingModel
                                );
                            }}
                            value={formikProps.values.tier}
                        >
                            {allowedTiers.map((value, index) => (
                                <option key={index} value={value}>
                                    {generateTierToTranslationMap(t).get(value)}
                                </option>
                            ))}
                        </select>
                        <button
                            className={classNames(style.tooltipPostion, buttons.textButton)}
                            onClick={() => setPricingTierModalVisible(true)}
                            type="button"
                        >
                            <div>
                                <PreviewIcon color={theme.iconFillColor} />
                                <span className={style.viewPricingButton}>
                                    {t("AddCustomerForm.viewFeaturesAndLicenses")}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>
                <PricingTierView
                    tenantType={formikProps.values.type}
                    visibility={pricingTierModalVisible}
                    setVisibility={setPricingTierModalVisible}
                    selectedTier={formikProps.values.tier}
                    tenantModal={formikProps.values.licensingModel}
                />
            </>
        );
    }

    function createCountrySection(formikProps: FormikProps<FormValues>) {
        return (
            <div className={form.formFields}>
                <label htmlFor="country" className={form.label}>
                    {t("Common.country")}:
                </label>

                <div className={form.formFieldsColumnDirection}>
                    <select
                        id="country"
                        className={classNames(form.select, form.fixedWidthInput, {
                            [form.inputError]: formikProps.errors.country,
                        })}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.countrySelect.itself}
                        onChange={formikProps.handleChange}
                        value={formikProps.values.country}
                    >
                        <option>{t("AddCustomerForm.SelectCountry")}</option>
                        {countries.map((value, index) => (
                            <option key={index} value={value.code}>
                                {value.name}
                            </option>
                        ))}
                    </select>
                    <div
                        className={form.error}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.countrySelect.errorLabel}
                    >
                        <ErrorMessage name="country" />
                    </div>
                </div>
            </div>
        );
    }

    function createContractTypeSection(formikProps: FormikProps<FormValues>) {
        const contractTypeToTranslationKeyMap = new Map<ContractType, string>([
            ["TRIAL", t("AddCustomerForm.contractType.trial")],
            ["PRODUCTION", t("AddCustomerForm.contractType.production")],
        ]);
        const handleOkClick = (contractType: ContractType) => {
            setSelectedContractType(contractType);
            setSelectedExpirationDate(contractType === "TRIAL" ? defaultTrialExpiration : undefined);
            setSelectedDateAsString(
                contractType === "TRIAL" ? formatDatePickerDate(defaultTrialExpiration) : t("Common.never")
            );
            formikProps.setFieldValue("contractType", contractType);
            formikProps.setFieldValue("expirationDate", contractType === "TRIAL" ? defaultTrialExpiration : undefined);
            formikProps.setFieldValue("automaticDeletion", contractType === "TRIAL");
        };

        const contractTypes = generateContractTypeOptions();
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <label htmlFor="contractType" className={classNames(form.label)}>
                        {t("AddCustomerForm.usecaseType")}
                    </label>
                    <select
                        id="contractType"
                        className={classNames(form.select, form.fixedWidthInput)}
                        onChange={(event) => handleOkClick(event.target.value as ContractType)}
                        value={selectedContractType}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.contractTypeSelect.itself}
                    >
                        {contractTypes.map((value, index) => (
                            <option key={index} value={value}>
                                {contractTypeToTranslationKeyMap.get(value)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }

    function createAutomaticDeletionSection(formikProps: FormikProps<FormValues>, value: boolean) {
        return (
            <>
                {
                    <AutomaticDeletion
                        selectedContractType={selectedContractType}
                        handleChange={(checked) => {
                            formikProps.setFieldValue("automaticDeletion", checked);
                        }}
                        theme={props.theme}
                        isAutomaticDeletionEnabled={formikProps.values.contractType === "TRIAL"}
                        collectValue={setAutomaticDeletionDefaultValue}
                        value={value}
                    />
                }
            </>
        );
    }

    function createNotesSection(formikProps: FormikProps<FormValues>) {
        const notesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setState((prevState) => ({
                ...prevState,
                notesCharactersLeft: event.target.maxLength - event.target.value.length,
            }));
        };

        return (
            <div className={form.formFields}>
                <div className={form.formFieldsColumnDirection}>
                    <span className={form.optional}>{t("Common.optional")}</span>
                    <label htmlFor="notes" className={form.label}>
                        {t("Common.notes")}:
                    </label>
                </div>
                <div className={style.notesContainer}>
                    <div className={form.formFieldsColumnDirection}>
                        <textarea
                            id="notes"
                            className={classNames(
                                form.input,
                                form.fixedWidthInput,
                                { [form.inputError]: formikProps.errors.notes },
                                style.notesHeight
                            )}
                            maxLength={NOTES_MAX_LENGTH}
                            data-testid={testIds.workArea.tenant.manageTenantDialog.details.noteTextArea.itself}
                            onChange={(e) => {
                                formikProps.handleChange(e);
                                notesChangeHandler(e);
                            }}
                            value={formikProps.values.notes}
                        />
                        <span className={form.optional}>
                            {t("AddCustomerForm.charactersLeft", {
                                remainingCharacters: state.notesCharactersLeft.toString(),
                                notesMaxLength: NOTES_MAX_LENGTH.toString(),
                            })}
                        </span>
                        <div
                            className={form.error}
                            data-testid={testIds.workArea.tenant.manageTenantDialog.details.noteTextArea.errorLabel}
                        >
                            <ErrorMessage name="notes" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function createMainContactSection(formikProps: FormikProps<FormValues>) {
        return (
            <div className={classNames(form.formFields, style.topMarginGap)}>
                <label htmlFor="mainContactName" className={form.label}>
                    {t("AddCustomerForm.contactName")}
                </label>

                <div className={form.formFieldsColumnDirection}>
                    <input
                        id="mainContactName"
                        className={classNames(form.input, form.fixedWidthInput, {
                            [form.inputError]: formikProps.errors.mainContactName,
                        })}
                        maxLength={NAME_MAX_LENGTH}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.contactNameInput.itself}
                        onChange={formikProps.handleChange}
                        value={formikProps.values.mainContactName}
                    />
                    <div
                        className={form.error}
                        data-testid={testIds.workArea.tenant.manageTenantDialog.details.contactNameInput.errorLabel}
                    >
                        <ErrorMessage name="mainContactName" />
                    </div>
                </div>
            </div>
        );
    }

    function createEmailSection(formikProps: FormikProps<FormValues>) {
        const loader = state.miniLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );
        return (
            <div className={form.formFields}>
                <label htmlFor="email" className={form.label}>
                    {t("Common.email")}:
                </label>
                <Box verticalAlignment="Center">
                    <div className={form.formFieldsColumnDirection}>
                        <input
                            id="email"
                            className={classNames(form.input, form.fixedWidthInput, {
                                [form.inputError]: formikProps.errors.email,
                            })}
                            maxLength={EMAIL_MAX_LENGTH}
                            data-testid={testIds.workArea.tenant.manageTenantDialog.details.contactEmailInput.itself}
                            onChange={formikProps.handleChange}
                            value={formikProps.values.email}
                        />
                        {loader}
                        <div
                            className={form.error}
                            data-testid={
                                testIds.workArea.tenant.manageTenantDialog.details.contactEmailInput.errorLabel
                            }
                        >
                            <ErrorMessage name="email" />
                        </div>
                    </div>

                    <Tooltip
                        content={
                            <div className={style.tooltipOpen}>{t("AddCustomerForm.contactEmailInfoToolTip")}</div>
                        }
                        placement={"top"}
                    >
                        <div className={style.info}>
                            <Info borderColor={props.theme.contentBackgroundColor} color={props.theme.iconFillColor} />
                        </div>
                    </Tooltip>
                </Box>
            </div>
        );
    }

    function handleDefaultDateSelection(formikProps: FormikProps<FormValues>) {
        if (selectedContractType === "TRIAL") {
            setSelectedDateAsString(formatDatePickerDate(defaultTrialExpiration));
            setSelectedExpirationDate(defaultTrialExpiration);
            formikProps.setFieldValue("expirationDate", defaultTrialExpiration);
        } else {
            setSelectedDateAsString(t("Common.never"));
            setSelectedExpirationDate(undefined);
            formikProps.setFieldValue("expirationDate", null);
        }
    }

    function handleExpirationDatesSelection(formikProps: FormikProps<FormValues>) {
        if (props.parentExpirationDate) {
            if (selectedContractType === "PRODUCTION") {
                setSelectedDateAsString(formatDatePickerDate(props.parentExpirationDate));
                setSelectedExpirationDate(props.parentExpirationDate);
                formikProps?.setFieldValue("expirationDate", props.parentExpirationDate);
            } else {
                setSelectedExpirationDate(defaultTrialExpiration);
                setSelectedDateAsString(formatDatePickerDate(defaultTrialExpiration));
                formikProps?.setFieldValue("expirationDate", defaultTrialExpiration);
            }
        } else {
            handleDefaultDateSelection(formikProps);
        }
    }

    function createAccessExpirySection(formikProps: FormikProps<FormValues>): JSX.Element {
        const handleOkClick = (date: Date) => {
            setCleaned(false);
            setSelectedExpirationDate(date);
            setSelectedDateAsString(formatDatePickerDate(date));
            formikProps.setFieldValue("expirationDate", date);
        };
        const handleClean = () => {
            setCleaned(true);
            handleExpirationDatesSelection(formikProps);
        };
        const createYesterday = () => {
            const date = new Date();
            date.setDate(date.getDate() - 1);
            return date;
        };
        const setDefaultDate = () => {
            if (props.parentExpirationDate) {
                if (!cleaned) {
                    if (selectedExpirationDate && props.parentExpirationDate >= selectedExpirationDate) {
                        return selectedExpirationDate;
                    }
                } else {
                    if (selectedContractType === "TRIAL") {
                        return selectedExpirationDate;
                    }
                    setSelectedExpirationDate(props.parentExpirationDate);
                    return props.parentExpirationDate;
                }
            } else {
                if (!cleaned) {
                    if (selectedExpirationDate) {
                        return selectedExpirationDate;
                    }
                } else {
                    if (selectedContractType === "TRIAL") {
                        return selectedExpirationDate;
                    }
                }
            }
            return null;
        };

        const isDateDisabled = (date: Date) => {
            const maxDate = new Date(formatDateWithoutTime(maxDateString));
            if (props.parentExpirationDate == null) {
                return isBefore(date, createYesterday()) || isAfter(date, new Date(toUtcEndOfDay(maxDate)));
            }

            date.setHours(0, 0, 0, 0);
            return isBefore(date, createYesterday()) || isAfter(date, props.parentExpirationDate);
        };

        const calendar = document.getElementsByClassName("rs-picker-toggle-caret rs-icon")[0] as HTMLElement;
        if (calendar) {
            calendar.setAttribute("aria-label", t("AltText.calendar"));
        }
        return (
            <>
                {
                    <div className={form.formFields}>
                        <div className={form.formFieldsColumnDirection}>
                            {selectedContractType === "PRODUCTION" && (
                                <span className={form.optional}>{t("Common.optional")}</span>
                            )}
                            <label htmlFor="expirationDate" className={form.label}>
                                {t("Common.accessExpiry")}:
                            </label>
                        </div>
                        <DatePicker
                            id="expirationDate"
                            caretAs={(colorProps) => <CalendarIcon {...colorProps} color={props.theme.iconFillColor} />}
                            data-testid={testIds.workArea.tenant.manageTenantDialog.settings.datePickerInput}
                            shouldDisableDate={isDateDisabled}
                            ranges={[
                                {
                                    label: "Today",
                                    value: new Date(),
                                    closeOverlay: true,
                                },
                            ]}
                            title={t("AltText.calendar")}
                            placeholder={selectedDateAsString}
                            onSelect={handleOkClick}
                            onClean={handleClean}
                            placement={"top"}
                            className={form.dateRange}
                            renderValue={(value: Date) => {
                                return selectedExpirationDate == null
                                    ? t("Common.never")
                                    : `${formatDateWithoutTime(value.toString())}`;
                            }}
                            value={setDefaultDate()}
                        />
                    </div>
                }
            </>
        );
    }

    function createSalesforceAccountId(formikProps: FormikProps<FormValues>) {
        const loader = state.miniLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <label htmlFor="salesforceAccountId" className={form.label}>
                        {t("AddCustomerForm.accountId")}
                    </label>
                    <Box verticalAlignment="Center">
                        <div className={form.formFieldsColumnDirection}>
                            <input
                                id="salesforceAccountId"
                                className={classNames(form.input, form.fixedWidthInput, {
                                    [form.inputError]: formikProps.errors.salesforceAccountId,
                                })}
                                data-testid={
                                    testIds.workArea.tenant.manageTenantDialog.details.salesforceAccountIdInput.itself
                                }
                                onChange={(event) => {
                                    formikProps.handleChange(event);
                                    if (state.emsIdUseDefault) {
                                        formikProps.setFieldValue("ems.emsId", event.target.value);
                                    }
                                }}
                                value={formikProps.values.salesforceAccountId}
                            />
                            {loader}
                            <div
                                className={form.error}
                                data-testid={
                                    testIds.workArea.tenant.manageTenantDialog.details.salesforceAccountIdInput
                                        .errorLabel
                                }
                            >
                                <ErrorMessage name="salesforceAccountId" />
                            </div>
                        </div>
                        <Tooltip
                            content={<div className={style.tooltipOpen}>{t("AddCustomerForm.accountIdTooltip")}</div>}
                            placement={"top"}
                        >
                            <span className={style.info}>
                                <Info
                                    borderColor={props.theme.contentBackgroundColor}
                                    color={props.theme.iconFillColor}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                </div>
            </div>
        );
    }

    function createSalesForceContractId(formikProps: FormikProps<FormValues>) {
        const loader = state.miniLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <div className={form.formFieldsColumnDirection}>
                        <span className={form.optional}>{t("Common.optional")}</span>
                        <label htmlFor="salesforceContractId" className={form.label}>
                            {t("AddCustomerForm.salesforceContractId")}
                        </label>
                    </div>

                    <div className={form.formFieldsColumnDirection}>
                        <input
                            id="salesforceContractId"
                            className={classNames(form.input, form.fixedWidthInput, {
                                [form.inputError]: formikProps.errors.salesforceContractId,
                            })}
                            data-testid={
                                testIds.workArea.tenant.manageTenantDialog.details.salesforceContractIdInput.itself
                            }
                            value={formikProps.values.salesforceContractId}
                            onChange={(event) => {
                                formikProps.handleChange(event);
                                formikProps.setFieldValue("salesforceContractId", event.target.value.trim());
                            }}
                        />
                        {loader}
                        <div
                            className={form.error}
                            data-testid={
                                testIds.workArea.tenant.manageTenantDialog.details.salesforceContractIdInput.errorLabel
                            }
                        >
                            <ErrorMessage name="salesforceContractId" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function createSalesForceLeadId(formikProps: FormikProps<FormValues>) {
        const loader = state.miniLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );

        const handleOkClick = (state: string) => {
            formikProps.setFieldValue("salesforceLeadId", state);
        };
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <div className={form.formFieldsColumnDirection}>
                        {selectedContractType === "PRODUCTION" ? (
                            <span className={form.optional}>{t("Common.optional")}</span>
                        ) : null}
                        <label htmlFor="salesforceLeadId" className={form.label}>
                            {t("AddCustomerForm.salesforceLeadId")}
                        </label>
                    </div>

                    <div className={form.formFieldsColumnDirection}>
                        <input
                            id="salesforceLeadId"
                            className={classNames(form.input, form.fixedWidthInput, {
                                [form.inputError]: formikProps.errors.salesforceLeadId,
                            })}
                            data-testid={
                                testIds.workArea.tenant.manageTenantDialog.details.salesforceLeadIdInput.itself
                            }
                            value={formikProps.values.salesforceLeadId}
                            onChange={(event) => handleOkClick(event.target.value)}
                        />
                        {loader}
                        <div
                            className={form.error}
                            data-testid={
                                testIds.workArea.tenant.manageTenantDialog.details.salesforceLeadIdInput.errorLabel
                            }
                        >
                            <ErrorMessage name="salesforceLeadId" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function contractStateToTranslationKey(t: TFunction) {
        return new Map<TenantContractStatus, string>([
            [TenantContractStatus.SELECT, t("Common.select")],
            [TenantContractStatus.DRAFT, t("EditCustomerView.salesforceContractStatus.draft")],
            [TenantContractStatus.ACTIVE, t("EditCustomerView.salesforceContractStatus.active")],
            [TenantContractStatus.EXPIRED, t("EditCustomerView.salesforceContractStatus.expired")],
            [TenantContractStatus.TERMINATED, t("EditCustomerView.salesforceContractStatus.terminated")],
        ]);
    }

    function createCustomerContractState(formikProps: FormikProps<FormValues>) {
        const contractStatus = [
            TenantContractStatus.SELECT,
            TenantContractStatus.DRAFT,
            TenantContractStatus.ACTIVE,
            TenantContractStatus.EXPIRED,
            TenantContractStatus.TERMINATED,
        ];

        const handleOkClick = (state: string) => {
            setSelectedSalesforceContractState(state);
            formikProps.setFieldValue("salesforceContractState", state);
        };

        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <div className={form.formFieldsColumnDirection}>
                        <span className={form.optional}>{t("Common.optional")}</span>
                        <label htmlFor="salesforceContractStatus" className={form.label}>
                            {t("AddCustomerForm.salesforceContractStatus.label")}
                        </label>
                    </div>
                    <select
                        id="salesforceContractStatus"
                        className={classNames(form.select, form.fixedWidthInput)}
                        data-testid={
                            testIds.workArea.tenant.manageTenantDialog.details.salesforceContractStatusSelect.itself
                        }
                        onChange={(event) => handleOkClick(event.target.value)}
                        value={selectedSalesforceContractState}
                    >
                        {contractStatus.map((value, index) => (
                            <option key={index} value={value}>
                                {contractStateToTranslationKey(t).get(value)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }

    function createCustomerContactEmail(formikProps: FormikProps<FormValues>) {
        const loader = state.miniLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <div className={form.formFieldsColumnDirection}>
                        <span className={form.optional}>{t("Common.optional")}</span>
                        <label htmlFor="salesforceContactEmail" className={form.label}>
                            {t("AddCustomerForm.salesforceContactEmail")}
                        </label>
                    </div>
                    <Box verticalAlignment="Center">
                        <div className={form.formFieldsColumnDirection}>
                            <input
                                id="salesforceContactEmail"
                                className={classNames(form.input, form.fixedWidthInput, {
                                    [form.inputError]: formikProps.errors.salesforceContactEmail,
                                })}
                                maxLength={EMAIL_MAX_LENGTH}
                                data-testid={
                                    testIds.workArea.tenant.manageTenantDialog.details.salesforceContractEmailInput
                                        .itself
                                }
                                onChange={(event) => {
                                    formikProps.handleChange(event);
                                    formikProps.setFieldValue("salesforceContactEmail", event.target.value.trim());
                                }}
                                value={formikProps.values.salesforceContactEmail}
                            />
                            {loader}
                            <div
                                className={form.error}
                                data-testid={
                                    testIds.workArea.tenant.manageTenantDialog.details.salesforceContractEmailInput
                                        .errorLabel
                                }
                            >
                                <ErrorMessage name="salesforceContactEmail" />
                            </div>
                        </div>
                        <Tooltip
                            content={
                                <div className={style.tooltipOpen}>{t("AddCustomerForm.contactEmailInfoToolTip")}</div>
                            }
                            placement={"top"}
                        >
                            <span className={style.info}>
                                <Info
                                    borderColor={props.theme.contentBackgroundColor}
                                    color={props.theme.iconFillColor}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                </div>
            </div>
        );
    }

    function createCustomerContactEndDate(formikProps: FormikProps<FormValues>) {
        const calendar = document.getElementsByClassName("rs-picker-toggle-caret rs-icon")[0] as HTMLElement;
        if (calendar) {
            calendar.setAttribute("aria-label", t("AltText.calendar"));
        }
        function formatDate(date: Date): string {
            return date.getDate().toString() + " " + MONTHS[date.getMonth()] + " " + date.getFullYear();
        }
        const handleOkClick = (date: Date) => {
            setSalesforceContractEndDate(date);
            setSelectedContractEndDateAsString(formatDate(date));
            formikProps.setFieldValue("salesforceContractEndDate", date);
        };
        const handleClean = () => {
            setSelectedContractEndDateAsString(t("Common.select"));
            setSalesforceContractEndDate(undefined);
            formikProps.setFieldValue("salesforceContractEndDate", null);
        };
        const setDefaultDate = () => {
            if (salesforceContractEndDate === undefined) {
                return undefined;
            }
            return salesforceContractEndDate;
        };
        return (
            <div className={style.regionAndInfoContainer}>
                <div className={form.formFields}>
                    <div className={form.formFieldsColumnDirection}>
                        <span className={form.optional}>{t("Common.optional")}</span>
                        <label htmlFor="salesforceContractEndDate" className={form.label}>
                            {t("AddCustomerForm.salesforceContractEndDate")}
                        </label>
                    </div>
                    <DatePicker
                        id="salesforceContractEndDate"
                        data-testid={
                            testIds.workArea.tenant.manageTenantDialog.details.salesforceContractEndDateDatePickerInput
                                .itself
                        }
                        ranges={[
                            {
                                label: "Today",
                                value: new Date(),
                                closeOverlay: true,
                            },
                        ]}
                        title={t("AltText.calendar")}
                        placeholder={selectedContractEndDateAsString}
                        onSelect={handleOkClick}
                        onClean={handleClean}
                        placement={"top"}
                        className={form.dateRange}
                        renderValue={(value: Date) => {
                            return isStringBlank(value.toString())
                                ? t("Common.select")
                                : formatDateWithoutTime(value.toString());
                        }}
                        value={setDefaultDate()}
                    />
                </div>
            </div>
        );
    }

    function emsIdUseDefaultCheckbox(formikProps: FormikProps<FormValues>) {
        const loader = state.miniLoader && (
            <div className={style.loaderContainer}>
                <LoadingIndicator small={true} />
            </div>
        );
        const handleToggle = (): void => {
            setState((prevState) => ({
                ...prevState,
                emsIdUseDefault: !prevState.emsIdUseDefault,
            }));
            if (state.emsIdUseDefault) {
                formikProps.setFieldValue("ems.emsId", formikProps.values.salesforceAccountId);
            }
        };
        return (
            <div className={classNames(form.formFields, style.defaultValueCheckbox)}>
                {loader}
                <Box verticalAlignment="Center">
                    <label className={form.container}>
                        <input
                            type="checkbox"
                            id="emsIdUseDefault"
                            className={classNames(form.input)}
                            defaultChecked={state.emsIdUseDefault}
                            data-testid={testIds.workArea.tenant.manageTenantDialog.settings.emsIdUseDefaultCheckbox}
                            onChange={() => {
                                handleToggle();
                            }}
                        />
                        <span className={form.checkmark} />
                    </label>
                    <label htmlFor="emsIdUseDefault" className={classNames(form.label)}>
                        {t("AddCustomerForm.useDefaultValue")}
                    </label>
                </Box>
            </div>
        );
    }

    function createLicenceTable(formikProps: FormikProps<FormValues>) {
        return !toCreateDeliveryTab ? null : (
            <TabPanel>
                <DeliveryTab
                    defaultTenantLicenses={props.tenantLicenses}
                    tier={formikProps.values.tier}
                    licensingModel={formikProps.values.licensingModel}
                    selectedLicenses={state.selectedLicenses}
                    setSelectedLicenses={(licenses) =>
                        setState((prevState) => ({
                            ...prevState,
                            selectedLicenses: licenses,
                        }))
                    }
                    deliveryDetails={formikProps.values.deliveryDetails}
                    errors={formikProps.errors}
                    handleChange={formikProps.handleChange}
                    inEdit={false}
                    allTenantLicenses={[]}
                    rateVersions={props.rateVersions}
                    tenantType={formikProps.values.type}
                    parentDrawbackPrevention={props.parentDrawbackPrevention}
                    contractType={formikProps.values.contractType}
                />
            </TabPanel>
        );
    }

    function updateMiniLoader(miniLoader: boolean): void {
        setState((prevState) => ({
            ...prevState,
            miniLoader: miniLoader,
        }));
    }

    const getSubmitHandler = (values: FormValues) => {
        if (selectedContractType === "PRODUCTION") {
            values.automaticDeletion = false;
        }

        if (values.deliveryDetails.targetDate) {
            values.deliveryDetails.targetDate = new Date(values.deliveryDetails.targetDate)
                .toISOString()
                .replace(/\.\d{3}Z$/, "Z");
        } else {
            delete values.deliveryDetails.targetDate;
        }
        setState((prevState) => {
            return {
                ...prevState,
                formValues: {
                    ...prevState.formValues,
                    expirationDate: values.expirationDate,
                },
            };
        });
        const buttonDisabled: boolean = isAnyLicenseInvalid(state.selectedLicenses) || isSubmitDisabled;
        if (buttonDisabled) {
            return;
        } else {
            props.submitEventHandler(
                { ...values, ems: { ...values.ems, emsId: values.ems.emsId.trim() } },
                state.selectedLicenses
            );
        }
    };

    // The following arrays (generalInputLabels, settingsInputLabels and licenseDeliveryInputLabels) contain the required form fields and other fields that needs to be validated.
    // These field names are derived from the key names of their respective interfaces.
    // Whenever a new required field or other fields that need to pass validation is added to these interfaces, these arrays need to be updated as well.
    const generalInputLabels: (keyof GeneralRequiredFormValues)[] = [
        "email",
        "country",
        "customerName",
        "mainContactName",
        "salesforceAccountId",
        "region",
        "salesforceLeadId",
    ];

    const settingsInputLabels: (keyof SettingsFormValues)[] = ["ems"];

    const licenseDeliveryInputLabels: (keyof DeliveryDetailsFormValues)[] = ["deliveryDetails"];

    const highlightTabWithError = (
        errors: FormikErrors<FormValues>,
        inputLabels: string[],
        text: string,
        invalidLicenses?: boolean
    ) => {
        let foundErrors = false;

        for (const each of Object.keys(errors)) {
            if (inputLabels.includes(each)) {
                foundErrors = true;
                break;
            }
        }
        if (invalidLicenses) {
            foundErrors = true;
        }
        return (
            <div className={style.gridColumns}>
                <div
                    className={classNames({
                        [form.inputError]: foundErrors,
                    })}
                >
                    {foundErrors ? (
                        <FailedRedNotificationIcon
                            backgroundColor={props.theme.errorIconColor}
                            iconColor={props.theme.contentBackgroundColor}
                        />
                    ) : null}
                </div>
                <div
                    className={classNames({
                        [style.tabTitle]: foundErrors,
                    })}
                >
                    {text}
                </div>
            </div>
        );
    };

    const emailTaggingTest = (value?: string) => {
        if (!value) {
            return true;
        }
        const localPart = value.split("@")[0];
        const plusCount = (localPart.match(/\+/g) || []).length;
        // Allow maximum one + for tagging
        return plusCount <= 1;
    };

    const emailPlusEndingTest = (value?: string) => {
        if (!value) {
            return true;
        }
        const localPart = value.split("@")[0];
        return !localPart.endsWith("+");
    };

    const emailPlusStartingTest = (value?: string) => {
        if (!value) {
            return true;
        }
        const localPart = value.split("@")[0];
        return !localPart.startsWith("+");
    };

    const validateEmail = () => {
        return isUserInternal()
            ? string()
                  .email(t("Common.invalidEmail"))
                  .required(t("AddCustomerForm.contactEmailRequired"))
                  .max(EMAIL_MAX_LENGTH)
                  .test("email-tagging", t("Common.invalidEmailTagging"), emailTaggingTest)
                  .test("email-plus-ending", t("Common.emailCannotEndWithPlus"), emailPlusEndingTest)
                  .test("email-plus-starting", t("Common.emailCannotStartWithPlus"), emailPlusStartingTest)
                  .test("exists", t("Common.emailNotAvailable"), function (value) {
                      updateMiniLoader(true);
                      if (value == null) {
                          return Promise.resolve(true);
                      }
                      const emailToBeValidated = value.trim();
                      const abortController = new AbortController();
                      return userService
                          .checkEmailAvailability(emailToBeValidated, abortController)
                          .then((result: CheckEmailAvailability) => {
                              if (result.emailIsUndeliverable) {
                                  return this.createError({
                                      message: t("Common." + result.errorMessage, {
                                          domain_name: result.domainName,
                                      }),
                                  });
                              }
                              return result.emailIsAvailable;
                          })
                          .finally(() => updateMiniLoader(false));
                  })
            : string()
                  .email(t("Common.invalidEmail"))
                  .max(EMAIL_MAX_LENGTH)
                  .test("email-tagging", t("Common.invalidEmailTagging"), emailTaggingTest)
                  .test("email-plus-ending", t("Common.emailCannotEndWithPlus"), emailPlusEndingTest)
                  .test("email-plus-starting", t("Common.emailCannotStartWithPlus"), emailPlusStartingTest)
                  .test("exists", t("Common.emailNotAvailable"), function (value) {
                      updateMiniLoader(true);
                      if (value == null) {
                          return Promise.resolve(true);
                      }
                      const emailToBeValidated = value.trim();
                      const abortController = new AbortController();
                      return userService
                          .checkEmailAvailability(emailToBeValidated, abortController)
                          .then((result: CheckEmailAvailability) => {
                              if (result.emailIsUndeliverable) {
                                  return this.createError({
                                      message: t("Common." + result.errorMessage, {
                                          domain_name: result.domainName,
                                      }),
                                  });
                              }
                              return result.emailIsAvailable;
                          })
                          .finally(() => updateMiniLoader(false));
                  });
    };

    const validateContractEmailId = () => {
        return isUserParentInternal()
            ? string()
                  .email(t("Common.invalidEmail"))
                  .max(EMAIL_MAX_LENGTH)
                  .test("exists", t("Common.emailNotAvailable"), function (value) {
                      if (value == null) {
                          return Promise.resolve(true);
                      }
                      const emailToBeValidated = value.trim();
                      const abortController = new AbortController();
                      return userService
                          .checkEmailAvailability(emailToBeValidated, abortController)
                          .then((result: CheckEmailAvailability) => {
                              if (result.emailIsUndeliverable) {
                                  return this.createError({
                                      message: t("Common." + result.errorMessage, {
                                          domain_name: result.domainName,
                                      }),
                                  });
                              }
                              return result.emailIsAvailable;
                          });
                  })
            : string();
    };

    const validateContractId = () => {
        const pattern = /^(?:800.{15}|TEST.{14})$/;

        return string().test(
            "exists",
            t("AddCustomerForm.salesforceContractIdPolicy", {
                length: GENERIC_ID_LENGTH_FOR_SALESFORCE,
                option1: SALESFORCE_CONTRACT_ID_VALIDATION_FORMAT_1,
                option2: GENERIC_ID_OPTION_FOR_SALESFORCE,
            }),
            (value) => {
                if (!value) {
                    return true;
                }

                return value.length === 18 && pattern.test(value);
            }
        );
    };

    const validateOpportunityId = () => {
        return string().test("conditional-required", t("AddCustomerForm.opportunityIdRequired"), function (value) {
            const { createError } = this;
            const isInternal = isUserParentInternal();
            const licenseType = state.selectedLicenses[0]?.licenseType;
            const caseNumber = this.parent?.caseNumber;

            const isRequired = isInternal && toCreateDeliveryTab && (licenseType !== "default" || !!caseNumber);

            if (!isRequired) {
                return true;
            }

            if (!value) {
                return createError({ message: t("AddCustomerForm.opportunityIdRequired") });
            }

            if (value.toLowerCase().startsWith("test")) {
                return true;
            }

            if (
                !value.startsWith(OPPORTUNITY_ID_VALIDATION_FORMAT_1) &&
                !value.toLowerCase().startsWith(OPPORTUNITY_ID_VALIDATION_FORMAT_2.toLowerCase())
            ) {
                return createError({
                    message: t("AddCustomerForm.opportunityIdFormat", {
                        option1: OPPORTUNITY_ID_VALIDATION_FORMAT_1,
                        option2: OPPORTUNITY_ID_VALIDATION_FORMAT_2,
                    }),
                });
            }
            if (value.length !== OPPORTUNITY_ID_LENGTH) {
                return createError({
                    message: t("AddCustomerForm.opportunityIdLength", { length: OPPORTUNITY_ID_LENGTH }),
                });
            }
            return true;
        });
    };

    const validateSalesforceAccountId = () => {
        return isUserInternal()
            ? string()
                  .test("salesforceAccountId", t("AddCustomerForm.accountIdRequired"), (value) => {
                      const isProduction = selectedContractType === CONTRACT_TYPE_PRODUCTION;
                      if (isProduction) {
                          return !!value;
                      }
                      return true;
                  })
                  .matches(
                      /^(001|test)/i,
                      t("DeliveryHistory.addLicenseDelivery.validation.salesforceIdFormat", {
                          option1: SALESFORCE_ID_VALIDATION_FORMAT_1,
                          option2: GENERIC_ID_OPTION_FOR_SALESFORCE,
                      })
                  )
                  .length(
                      SALESFORCE_ID_LENGTH,
                      t("DeliveryHistory.addLicenseDelivery.validation.salesforceIdLength", {
                          length: GENERIC_ID_LENGTH_FOR_SALESFORCE,
                      })
                  )
            : string()
                  .test(
                      "salesforceAccountIdFormatIfProvided",
                      t("DeliveryHistory.addLicenseDelivery.validation.salesforceIdFormat", {
                          option1: SALESFORCE_ID_VALIDATION_FORMAT_1,
                          option2: GENERIC_ID_OPTION_FOR_SALESFORCE,
                      }),
                      (value) => {
                          if (!value) {
                              return true;
                          }
                          return /^(001|test)/i.test(value);
                      }
                  )
                  .test(
                      "salesforceAccountIdLengthIfProvided",
                      t("DeliveryHistory.addLicenseDelivery.validation.salesforceIdLength", {
                          length: GENERIC_ID_LENGTH_FOR_SALESFORCE,
                      }),
                      (value) => {
                          if (!value) {
                              return true;
                          }
                          return value.length === SALESFORCE_ID_LENGTH;
                      }
                  );
    };

    const validateLeadId = () => {
        return isUserInternal() || selectedContractType === CONTRACT_TYPE_TRIAL
            ? string()
                  .test("requireEitherLeadOrAccountId", t("AddCustomerForm.leadIdRequired"), function (value) {
                      const { salesforceAccountId } = this.parent;
                      return !(selectedContractType === "TRIAL" && !value && !salesforceAccountId);
                  })
                  .matches(
                      /^(00Q[A-Za-z0-9]{15}|TEST[A-Za-z0-9]{14})$/i,
                      t("AddCustomerForm.salesforceLeadIdPolicy", {
                          length: GENERIC_ID_LENGTH_FOR_SALESFORCE,
                          option1: SALESFORCE_LEAD_ID_VALIDATION_FORMAT_1,
                          option2: GENERIC_ID_OPTION_FOR_SALESFORCE,
                      })
                  )
            : string();
    };

    return (
        <Formik
            initialValues={state.formValues}
            onSubmit={(values: FormValues) => getSubmitHandler(values)}
            validationSchema={object().shape({
                customerName: string()
                    .required(t("AddCustomerForm.nameRequired"))
                    .max(INPUT_MAX_LENGTH, t("EditCustomerView.customerNameTooLong", { max: INPUT_MAX_LENGTH }))
                    .test("exists", t("Common.nameNotAvailable"), (value: string): Promise<boolean> => {
                        if (value == null) {
                            return Promise.resolve(true);
                        }
                        setNameLoader(true);
                        const trimmed = value.trim();
                        const abortController = new AbortController();
                        return tenantService
                            .fetchTenants(abortController, null, "", "", "", trimmed)
                            .then((e: TenantsData) => {
                                return e.message as boolean;
                            })
                            .finally(() => setNameLoader(false));
                    }),
                region: string().required(t("AddCustomerForm.regionRequired")),
                country: string().required(t("AddCustomerForm.countryRequired")),
                notes: string().max(NOTES_MAX_LENGTH),
                contractType: string().required(),
                mainContactName: isUserInternal()
                    ? string().required(t("AddCustomerForm.contactNameRequired")).max(NAME_MAX_LENGTH)
                    : string().max(NAME_MAX_LENGTH),
                email: validateEmail(),
                expirationDate: string().default("AddCustomerForm.defaultValueSent"),
                salesforceAccountId: validateSalesforceAccountId(),
                salesforceContractId: validateContractId(),
                salesforceLeadId: validateLeadId(),
                salesforceContractState: string(),
                salesforceContactEmail: validateContractEmailId(),
                salesforceContractEndDate: string().default("AddCustomerForm.defaultValueSent"),
                ems: object().shape({
                    availableSlActivations: number().min(0),
                    emsId: string().test(
                        "exists",
                        t("EditCustomerView.emsIdDoesNotExist"),
                        (value: string): Promise<boolean> => {
                            updateMiniLoader(true);
                            if (value == null || value.trim() === "") {
                                updateMiniLoader(false);
                                return Promise.resolve(true);
                            }
                            const abortController = new AbortController();
                            return licenseService
                                .checkEmsCustomerIdExistence(value.trim(), abortController)
                                .finally(() => updateMiniLoader(false));
                        }
                    ),
                }),
                deliveryDetails: object().shape({
                    caseNumber: string()
                        .test("conditional-required", t("AddCustomerForm.caseNumberRequired"), function (value) {
                            const isInternal = isUserParentInternal();
                            const licenseType = state.selectedLicenses?.[0]?.licenseType ?? "default";
                            const opportunityId = this.parent?.opportunityId;
                            const isRequired =
                                isInternal && toCreateDeliveryTab && (licenseType !== "default" || !!opportunityId);

                            if (!isRequired) {
                                return true;
                            }

                            return !!value;
                        })
                        .typeError(t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"))
                        .max(
                            MAX_CASE_NUMBER_LENGTH,
                            t("DeliveryHistory.addLicenseDelivery.validation.caseNumberLength", {
                                length: MAX_CASE_NUMBER_LENGTH,
                            })
                        )
                        .matches(/^\d+$/, t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType")),

                    opportunityId: validateOpportunityId(),
                }),
            })}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit} id="addTenantForm">
                    <Tabs>
                        <TabList>
                            <Tab data-testid={testIds.workArea.tenant.manageTenantDialog.details.tab}>
                                {highlightTabWithError(
                                    formikProps.errors,
                                    generalInputLabels,
                                    t("AddCustomerForm.generic")
                                )}
                            </Tab>
                            <Tab data-testid={testIds.workArea.tenant.manageTenantDialog.settings.tab}>
                                {highlightTabWithError(formikProps.errors, settingsInputLabels, t("Common.settings"))}
                            </Tab>
                            {toCreateDeliveryTab && (
                                <Tab data-testid={testIds.workArea.tenant.manageTenantDialog.delivery.tab}>
                                    {highlightTabWithError(
                                        formikProps.errors,
                                        licenseDeliveryInputLabels,
                                        t("AddCustomerForm.deliveryDetails"),
                                        isAnyLicenseInvalid(state.selectedLicenses)
                                    )}
                                </Tab>
                            )}
                        </TabList>
                        <TabPanel>
                            <div>
                                <Heading tag={"div"} variant="SUBTITLE_1">
                                    {t("AddCustomerForm.generic")}
                                </Heading>
                                {createCustomerNameSection(formikProps)}
                                {isUserParentInternal() ? createContractTypeSection(formikProps) : null}
                                {isUserParentInternal() && selectedContractType === "TRIAL"
                                    ? createAutomaticDeletionSection(formikProps, automaticDeletionDefaultValue)
                                    : null}
                                {createLicensingModelSection(formikProps)}
                                {createCustomerTypeSection(formikProps)}
                                {createTierSection(formikProps)}
                                {createRegionSection(formikProps)}
                                {createCountrySection(formikProps)}
                                {createNotesSection(formikProps)}
                                <br />
                                <Heading tag={"div"} variant="SUBTITLE_1">
                                    {t("AddCustomerForm.details")}
                                </Heading>
                                {createMainContactSection(formikProps)}
                                {createEmailSection(formikProps)}
                                {isUserParentInternal() ? (
                                    <>
                                        <br />
                                        <Heading tag={"div"} variant="SUBTITLE_1">
                                            {t("AddCustomerForm.salesforceAccount")}
                                        </Heading>
                                        {createSalesforceAccountId(formikProps)}
                                        {createSalesForceContractId(formikProps)}
                                        {createSalesForceLeadId(formikProps)}
                                        {createCustomerContractState(formikProps)}
                                        {createCustomerContactEmail(formikProps)}
                                        {createCustomerContactEndDate(formikProps)}
                                    </>
                                ) : null}
                            </div>
                        </TabPanel>
                        <TabPanel>
                            <div>
                                {isUserParentInternal() ? (
                                    <>
                                        <Heading tag={"div"} variant="SUBTITLE_1">
                                            {t("EditCustomerView.entitlementSettings")}
                                        </Heading>
                                        <div className={style.formFieldGroup}>
                                            {createField(
                                                "emsId",
                                                t("AddCustomerForm.accountId"),
                                                testIds.workArea.tenant.manageTenantDialog.settings.accountIdInput
                                                    .itself,
                                                formikProps.values.ems.emsId,
                                                "text",
                                                formikProps.errors.ems?.emsId != null,
                                                false,
                                                formikProps.handleChange,
                                                testIds.workArea.tenant.manageTenantDialog.settings.accountIdInput
                                                    .errorLabel,
                                                state.emsIdUseDefault
                                            )}
                                            {emsIdUseDefaultCheckbox(formikProps)}
                                        </div>
                                        {createField(
                                            "hlEntitlements",
                                            t("EditCustomerView.hlEntitlements"),
                                            testIds.workArea.tenant.manageTenantDialog.settings.hlEntitlementsCheckbox,
                                            formikProps.values.ems.hlEntitlements,
                                            "checkbox",
                                            false,
                                            false,
                                            formikProps.handleChange
                                        )}
                                        {createField(
                                            "slEntitlements",
                                            t("EditCustomerView.slEntitlements"),
                                            testIds.workArea.tenant.manageTenantDialog.settings.slEntitlementsCheckbox,
                                            formikProps.values.ems.slEntitlements,
                                            "checkbox",
                                            false,
                                            false,
                                            formikProps.handleChange
                                        )}
                                        {createField(
                                            "availableSlActivations",
                                            t("EditCustomerView.availableSlActivations"),
                                            testIds.workArea.tenant.manageTenantDialog.settings
                                                .availableSlActivationsInput.itself,
                                            formikProps.values.ems.availableSlActivations,
                                            "number",
                                            formikProps.errors.ems?.availableSlActivations != null,
                                            false,
                                            formikProps.handleChange,
                                            testIds.workArea.tenant.manageTenantDialog.settings
                                                .availableSlActivationsInput.errorLabel
                                        )}
                                        <br />
                                    </>
                                ) : null}
                                {
                                    <Heading tag={"div"} variant="SUBTITLE_1">
                                        {t("AddCustomerForm.status")}
                                    </Heading>
                                }
                                {createAccessExpirySection(formikProps)}

                                {isUserParentInternal() && (
                                    <>
                                        <Heading tag={"div"} variant="SUBTITLE_1">
                                            {t("AddCustomerForm.permissionsLW")}
                                        </Heading>
                                        <DrawbackPrevention
                                            drawbackPrevention={formikProps.values.drawbackPrevention ?? false}
                                            handleChange={(checked) => {
                                                formikProps.setFieldValue("drawbackPrevention", checked);
                                            }}
                                            theme={props.theme}
                                        />
                                    </>
                                )}
                            </div>
                        </TabPanel>
                        {createLicenceTable(formikProps)}
                    </Tabs>
                    <button type="submit" disabled={isSubmitDisabled}></button>
                </form>
            )}
        </Formik>
    );
};
